import { z } from 'zod';
/**
 * Papéis dentro de um escritório de contabilidade. Três, e não os cinco do
 * Adonai: aqui não há chão de fábrica. O `assistente` cadastra e prepara; só
 * `contador` e `administrador` geram ato, porque o ato é peça jurídica
 * assinada por quem tem registro no CRC.
 */
export const PAPEIS = ['administrador', 'contador', 'assistente'];
export const papelSchema = z.enum(PAPEIS);
/**
 * Como o papel aparece na tela.
 *
 * Separado de `PAPEIS` porque a chave é contrato de API — muda o banco e o
 * token — enquanto o rótulo é texto, e texto muda por pedido do cliente numa
 * terça-feira.
 */
export const ROTULO_PAPEL = {
    administrador: 'Administrador',
    contador: 'Contador',
    assistente: 'Assistente',
};
/** Quem pode levar um ato de RASCUNHO a GERADO. */
export function podeGerarAto(papel) {
    return papel === 'administrador' || papel === 'contador';
}
export const entrarSchema = z.object({
    email: z.string().email(),
    senha: z.string().min(8),
});
/**
 * AUTOCADASTRO de escritório — a porta pela qual um contador cria a própria
 * conta, sem passar por ninguém.
 *
 * ## Por que a senha exige 12, e não 8 como no login
 * `entrarSchema` valida a senha que já EXISTE: apertá-lo trancaria para fora
 * quem se cadastrou antes da regra. Aqui a senha está NASCENDO, e é o único
 * momento em que dá para exigir mais sem quebrar ninguém. Doze caracteres sem
 * exigência de símbolo é a recomendação corrente (NIST SP 800-63B): comprimento
 * protege mais do que a mistura obrigatória, que só produz "Senha@123".
 *
 * ## Por que o aceite é um campo, e não um checkbox só na tela
 * Aceite que mora na tela não chega ao servidor, e o que não chega ao servidor
 * não aconteceu. `literal(true)` recusa o `false` no schema — não há como
 * gravar um escritório sem o aceite registrado.
 */
export const criarEscritorioSchema = z.object({
    nomeEscritorio: z.string().trim().min(3).max(120),
    nomeAdministrador: z.string().trim().min(3).max(120),
    email: z.string().email().max(200),
    senha: z.string().min(12).max(200),
    aceiteTermos: z.literal(true),
});
export const refreshSchema = z.object({ refresh: z.string().min(20) });
/**
 * Quem está logado.
 *
 * O token carrega `tenantId`, `usuarioId` e `papel` — o suficiente para
 * AUTORIZAR, e nada para MOSTRAR. Nome e escritório vêm daqui, de uma leitura
 * do banco, porque token é dado do cliente e nome em token vira nome que o
 * cliente escolhe.
 */
export const euSchema = z.object({
    id: z.string().uuid(),
    nome: z.string().min(1),
    email: z.string().email(),
    papel: z.enum(PAPEIS),
    escritorio: z.object({ id: z.string().uuid(), nome: z.string().min(1) }),
});
/**
 * O que a pessoa pode mudar dos PRÓPRIOS dados.
 *
 * ## O que NÃO está aqui, e por quê
 * - **`papel`**: quem muda o papel de alguém é um administrador, na gestão de
 *   usuários. Deixar o campo aqui seria dar a qualquer assistente o botão de
 *   se promover.
 * - **`escritorio`**: ninguém troca de escritório editando o próprio cadastro.
 * - **`email`**: é a CREDENCIAL de entrada e é única global. Trocá-lo sem
 *   confirmar o endereço novo deixaria a pessoa trancada para fora na
 *   primeira letra errada — e este produto ainda não envia e-mail. Quando
 *   enviar, entra aqui com confirmação.
 *
 * Sobra o nome, que é o que aparece nos documentos e no canto da tela.
 */
export const alterarPerfilSchema = z.object({
    nome: z.string().trim().min(3).max(120),
});
/**
 * Troca de senha pela própria pessoa.
 *
 * A senha ATUAL é obrigatória mesmo já havendo sessão válida: sessão aberta
 * num computador emprestado é o caso comum, e sem a senha atual quem senta na
 * cadeira troca a senha e toma a conta. O mesmo mínimo de 12 do autocadastro,
 * pelo mesmo motivo — é uma senha NASCENDO.
 */
export const trocarSenhaSchema = z.object({
    senhaAtual: z.string().min(1),
    senhaNova: z.string().min(12).max(200),
});
/**
 * Um usuário do escritório, como o administrador o vê na gestão.
 *
 * Traz `ativo` e `criadoEm`, que o `Eu` não tem: quem administra precisa
 * distinguir "não existe" de "existe e está desligado" — são coisas
 * diferentes, e a segunda é reversível.
 */
export const usuarioDoEscritorioSchema = z.object({
    id: z.string().uuid(),
    nome: z.string().min(1),
    email: z.string().email(),
    papel: z.enum(PAPEIS),
    ativo: z.boolean(),
    criadoEm: z.string(),
});
/** O administrador cria um usuário do próprio escritório. */
export const criarUsuarioSchema = z.object({
    nome: z.string().trim().min(3).max(120),
    email: z.string().email().max(200),
    /** Mesmo mínimo do autocadastro: é uma senha NASCENDO. */
    senha: z.string().min(12).max(200),
    papel: z.enum(PAPEIS),
});
/**
 * O que o administrador muda em OUTRO usuário.
 *
 * Não inclui e-mail nem senha: o e-mail é credencial e única global (ver
 * `alterarPerfilSchema`), e a senha tem rota própria — misturar troca de senha
 * num PATCH de perfil faria uma alteração de papel carregar credencial no
 * corpo sem necessidade.
 */
export const alterarUsuarioSchema = z.object({
    nome: z.string().trim().min(3).max(120).optional(),
    papel: z.enum(PAPEIS).optional(),
    ativo: z.boolean().optional(),
});
/**
 * O administrador define a senha de outro usuário — SEM a senha atual.
 *
 * ## Por que aqui não se pede a senha atual, e em `trocarSenhaSchema` sim
 * São duas operações diferentes com dois riscos diferentes. Na tela de conta,
 * quem digita é o dono e a senha atual prova que é ele — protege contra quem
 * senta numa cadeira com sessão aberta. Aqui quem digita é o ADMINISTRADOR,
 * que por definição não sabe a senha do outro; exigi-la tornaria a função
 * impossível, e o escritório voltaria a depender do suporte para toda senha
 * esquecida.
 *
 * A proteção equivalente é outra: só administrador chega nesta rota, o alvo é
 * sempre do MESMO tenant, e a troca fica registrada no log da aplicação.
 */
export const definirSenhaSchema = z.object({
    senhaNova: z.string().min(12).max(200),
});
/**
 * O OPERADOR define a senha de um usuário de qualquer escritório.
 *
 * Exige MOTIVO, diferente do `definirSenhaSchema` do administrador. A razão é
 * a distância: o administrador age dentro da própria casa e responde por ela;
 * o operador age na casa dos outros, e uma ação dessas sem motivo registrado é
 * indistinguível de abuso quando alguém for auditar. Todas as ações do
 * operador seguem essa regra — ver `contratar`, `suspender`, `reativar`.
 */
export const definirSenhaPeloOperadorSchema = z.object({
    usuarioId: z.string().uuid(),
    senhaNova: z.string().min(12).max(200),
    motivo: z.string().trim().min(10).max(500),
});
//# sourceMappingURL=papel.js.map